import React, { useState } from 'react'
import { Box, IconButton, Typography, Tooltip } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import {
  SoapTemplateItem as SoapItem,
  SoapType,
} from '@/features/internal/drugs/types/soapTemplate'
import {
  ConfirmDialog,
  NotionTextField,
} from '@/apps/latest/components/portal/notion'
import { SectionHeading } from '@/components/atoms/SectionHeading'
import { SECONDARY_ICON_BUTTON_STYLE } from '@/shared/constants/buttonStyles'
import { PORTAL_TEXT } from '@/apps/latest/shared/styles/portalTypography'

// SOAP種別の日本語表記を取得 (素の日本語。英語前置ラベルは使わない)
const getSoapTypeLabel = (soapType: SoapType): string => {
  switch (soapType) {
    case 'S':
      return '主観的情報'
    case 'O':
      return '客観的情報'
    case 'A':
      return '評価'
    case 'OP':
      return '観察計画'
    case 'EP':
      return '教育計画'
    case 'CP':
      return 'ケア計画'
    default:
      return soapType
  }
}

interface SoapTemplateItemProps {
  /**
   * SOAP項目データ
   */
  item: SoapItem

  /**
   * 編集モードかどうか
   */
  editMode?: boolean

  /**
   * テンプレート内容が変更されたときに呼び出されるコールバック関数
   */
  onContentChange: (content: string) => void

  /**
   * 削除ボタンがクリックされたときに呼び出されるコールバック関数
   */
  onDelete: () => void
}

/**
 * SOAPテンプレートの個別項目を表示・編集するコンポーネント
 */
const SoapTemplateItem: React.FC<SoapTemplateItemProps> = ({
  item,
  editMode = false,
  onContentChange,
  onDelete,
}) => {
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        mb: 1,
        width: '100%',
      }}
    >
      {/* 種別ラベルは SectionHeading の単一 spec (チップ化しない)。tooltip で日本語名を補う */}
      <Tooltip title={getSoapTypeLabel(item.soap_type)} placement="top">
        <Box sx={{ minWidth: 28, flexShrink: 0, pt: editMode ? 1 : 0.25 }}>
          <SectionHeading>{item.soap_type}</SectionHeading>
        </Box>
      </Tooltip>

      {editMode ? (
        <Box sx={{ ml: 1, flexGrow: 1 }}>
          <NotionTextField
            multiline
            value={item.content}
            onChange={(e) => onContentChange(e.target.value)}
            placeholder={`${getSoapTypeLabel(item.soap_type)}を入力`}
            aria-label={`${getSoapTypeLabel(item.soap_type)}の内容`}
          />
        </Box>
      ) : (
        <Typography
          sx={{
            ...PORTAL_TEXT.cellBody,
            ml: 1,
            flexGrow: 1,
            whiteSpace: 'pre-wrap',
          }}
        >
          {item.content}
        </Typography>
      )}

      {editMode && (
        <>
          <Tooltip title="削除" placement="top">
            <IconButton
              onClick={() => setDeleteConfirmOpen(true)}
              size="small"
              sx={{
                ml: 1,
                ...SECONDARY_ICON_BUTTON_STYLE,
              }}
              aria-label="SOAP項目を削除"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <ConfirmDialog
            open={deleteConfirmOpen}
            onClose={() => setDeleteConfirmOpen(false)}
            onConfirm={() => {
              setDeleteConfirmOpen(false)
              onDelete()
            }}
            title="SOAP項目の削除"
            message="このSOAP項目を削除しますか？"
            confirmLabel="削除する"
            variant="danger"
          />
        </>
      )}
    </Box>
  )
}

export default SoapTemplateItem
